import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  StackedBarColors,
  StackedBarImpactColors,
} from "@intelligems/ig-graphs/colors";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { AxisTick } from "@src/features/analytics/baseDashboard/chartComponents/AxisTick";
import {
  getStackedBarRadiusTb,
  getStackedBarStackId,
  getStackedDataMax,
  stackedDataMaxFormatter,
  valueAccessor,
} from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { IgStackedBarRichAnalyticsTooltip } from "@src/features/analytics/tooltips/IgStackedBarRichAnalyticsTooltip";
import { getStackedBarShouldShowAxisDecimals } from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { selectedVariations } from "@src/store/slices/variations";
import { StackedBarMetricData } from "@src/types/analytics";

interface IgStackedBarChartProps {
  config: GraphConfig;
  groupMetricData: StackedBarMetricData[];
  testDuration: number;
  onMetricDetailsClick?: () => void;
  onAddKeyMetricClick?: () => void;
  compareVariationId?: string;
  graphHeight?: number;
  setCellColor?: (params: {
    compareVariationId?: string;
    data: StackedBarMetricData;
    index: number;
  }) => string;
}

export const IgStackedBarChart = ({
  config,
  groupMetricData,
  testDuration,
  compareVariationId,
  graphHeight,
  setCellColor,
}: IgStackedBarChartProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const variations = useSelector(selectedVariations);
  const controlVariationId =
    variations.find((variation) => variation.isControl)?.id || "";
  const updatedConfig = {
    ...config,
    stackedBarLegendTitles:
      config.stackedBarLegendTitles || groupMetricData[0].allIds!,
  };
  const theme = useTheme();

  const graphValueFormatter = useGraphValueFormatter();

  const dataMax = stackedDataMaxFormatter(
    updatedConfig,
    getStackedDataMax(groupMetricData, updatedConfig)
  );

  const getCellFill = (group: StackedBarMetricData, index: number) => {
    if (setCellColor) {
      return setCellColor({ compareVariationId, data: group, index });
    }
    return group.variation_id === compareVariationId
      ? StackedBarImpactColors[index][0]
      : StackedBarImpactColors[index][1];
  };

  return (
    <ResponsiveContainer
      width="100%"
      height={graphHeight ?? (config.isImpactGraph ? 150 : 300)}
    >
      <BarChart
        barCategoryGap={"3%"}
        margin={{ top: 15, bottom: 0, right: -5, left: -20 }}
        data={groupMetricData}
      >
        <CartesianGrid
          style={{ stroke: theme.igBorders.core.disabled }}
          vertical={false}
        />

        <XAxis
          interval={0}
          tick={(props) => (
            <AxisTick
              config={config}
              graphValueFormatter={graphValueFormatter}
              groupMetricData={groupMetricData}
              {...props}
            />
          )}
          axisLine={
            config.isImpactGraph
              ? false
              : { stroke: theme.igBorders.core.disabled }
          }
          tickLine={false}
          dataKey="name"
          padding={{ right: 30 }}
        />
        <YAxis
          domain={["dataMin", dataMax]}
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: any) =>
            graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: updatedConfig.dataTypes,
              decimalAmountShown: getStackedBarShouldShowAxisDecimals(
                groupMetricData,
                updatedConfig
              )
                ? 2
                : undefined,
              shouldShorten: true,
            })!
          }
        />
        <Tooltip
          position={{ y: 145 }}
          content={
            <IgStackedBarRichAnalyticsTooltip
              config={updatedConfig}
              controlVariationId={controlVariationId}
              compareVariationId={compareVariationId}
              barData={groupMetricData}
              storeCurrencyConfig={storeCurrencyConfig!.value}
              testDuration={testDuration}
              setLegendIconColor={setCellColor}
            />
          }
          cursor={{ fill: "transparent" }}
          wrapperStyle={{ zIndex: 99999 }}
        />
        {updatedConfig?.stackedBarLegendTitles?.map((legend, index) => (
          <Bar
            key={legend}
            isAnimationActive={false}
            name={`${legend}`}
            dataKey={legend}
            maxBarSize={60}
            stackId={getStackedBarStackId(index, updatedConfig)}
            radius={getStackedBarRadiusTb(index, updatedConfig)}
          >
            {groupMetricData.map((group: StackedBarMetricData) => (
              <Cell key={`cell-${legend}`} fill={getCellFill(group, index)} />
            ))}

            <LabelList
              fill={theme.palette.text.secondary}
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "12px",
                fontWeight: 600,
              }}
              position={"top"}
              valueAccessor={(val: any) =>
                valueAccessor(val, index, updatedConfig, graphValueFormatter)
              }
            />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
